<script lang="ts">
	import { taskStore } from '@repo/sharedlib/src/stores/task.store';
	import { transcripts } from '@repo/sharedlib/src/stores/transcripts';
	import { Dialog as DialogPrimitive } from 'bits-ui';
	import { onMount } from 'svelte';
	import { get } from 'svelte/store';

	interface Props {
		open: boolean;
		itemId: string;
		itemType: 'memory' | 'draft' | 'thread' | 'start';
		x: number;
		y: number;
		onOpenChange?: (open: boolean) => void;
	}

	let { open = $bindable(false), itemId, itemType, x, y, onOpenChange }: Props = $props();

	let inputElement: HTMLInputElement | undefined = $state();
	let currentName = $state('');
	let saving = $state(false);
	let hasSaved = false;

	// Load current name when modal opens
	onMount(() => {
		if (itemId) {
			if (itemType === 'memory') {
				const transcript = get(transcripts).idMap.get(itemId);
				// Use editedName if available, otherwise fallback to name
				currentName = transcript?.editedName || transcript?.name || '';
			} else if (itemType === 'start') {
				const task = taskStore.getTask(itemId);
				currentName = task?.name || '';
			}
		}
	});

	// Also update when itemId changes or modal opens
	$effect(() => {
		if (open) {
			hasSaved = false;
			if (itemId) {
				if (itemType === 'memory') {
					const transcript = get(transcripts).idMap.get(itemId);
					currentName = transcript?.editedName || transcript?.name || '';
				} else if (itemType === 'start') {
					const task = taskStore.getTask(itemId);
					currentName = task?.name || '';
				}

				// Focus input when modal opens
				setTimeout(() => {
					inputElement?.focus();
				}, 10);
			}
		}
	});

	async function saveName() {
		if (saving || hasSaved || !open) return;

		const nameToSave = currentName.trim();

		// Optimistically close modal immediately
		hasSaved = true;
		saving = true;
		open = false;
		onOpenChange?.(false);

		if (!nameToSave) {
			saving = false;
			return;
		}

		try {
			if (itemType === 'memory') {
				// Update editedName instead of name
				// doSync=true but we don't await strictly for UI closing
				transcripts.updateTranscript(itemId, { editedName: nameToSave }, true, true);
			} else if (itemType === 'start') {
				await taskStore.updateTask(itemId, { name: nameToSave });
			}
		} catch (error) {
			console.error('Failed to save name:', error);
			// Ideally revert UI here if needed, but for now just log
		} finally {
			saving = false;
		}
	}

	function handleKeydown(event: KeyboardEvent) {
		if (event.key === 'Enter') {
			event.preventDefault();
			saveName();
		} else if (event.key === 'Escape') {
			event.preventDefault();
			hasSaved = true; // Prevent blur from saving
			open = false;
			onOpenChange?.(false);
		}
	}

	function handleOpenChange(isOpen: boolean) {
		if (!isOpen && open && !hasSaved) {
			// Save when closing if not already saved
			saveName();
		}
		open = isOpen;
		onOpenChange?.(isOpen);
	}
</script>

<DialogPrimitive.Root {open} onOpenChange={handleOpenChange}>
	<DialogPrimitive.Portal>
		<DialogPrimitive.Overlay class="fixed inset-0 z-50 bg-transparent" />
		<DialogPrimitive.Content
			onInteractOutside={(e) => {
				e.preventDefault();
				saveName();
			}}
			class="fixed z-[51] rounded-lg border border-gray-300 bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-800"
			style="left: {x}px; top: {y}px; transform: none;"
		>
			<input
				bind:this={inputElement}
				bind:value={currentName}
				onkeydown={handleKeydown}
				onblur={() => {
					// Add small delay to allow click events on the overlay/outside to process
					setTimeout(() => {
						if (open && !hasSaved) {
							saveName();
						}
					}, 150);
				}}
				type="text"
				class="w-64 rounded border-none bg-transparent px-2 py-1 text-sm text-gray-900 outline-none focus:ring-0 dark:text-gray-100"
				placeholder="Enter name..."
				disabled={saving}
			/>
		</DialogPrimitive.Content>
	</DialogPrimitive.Portal>
</DialogPrimitive.Root>
